<template>
  <div
    v-if="items.length > 0"
    class="m-related-search-results"
    :data-testid="cypressTag"
  >
    <!-- heading and subheading -->
    <div v-if="heading" class="m-related-search-results__header">
      <div class="m-related-search-results__heading-container">
        <h2
          class="m-related-search-results__heading"
          data-testid="related-items-heading"
        >
          {{ heading }}
        </h2>
        <h3 v-if="subheading" class="m-related-search-results__subheading">
          {{ subheading }}
        </h3>
      </div>
      <!-- browse all link -->
      <GuiLink
        v-if="items.length > 6 && showBrowseAll"
        class="m-related-search-results__link"
        :to="{
          name: 'search',
          query: searchParamsForQuery
        }"
      >
        {{ browseAllLabel }}
      </GuiLink>
    </div>
    <div
      v-if="showCount && resultsCountLabel"
      class="m-related-search-results__carousel-count"
    >
      {{ resultsCountLabel }}
    </div>
    <ListingCarousel
      :items="items"
      size="xsmall"
      data-testid="carousel"
      class="m-related-search-results__carousel"
    >
      <template #default="{ item }">
        <div
          v-if="item.cta == undefined || item.cta == false"
          class="m-related-search-results__carousel-item"
          :data-testid="'carousel-item-' + item.id"
        >
          <ImageAndCaption
            v-bind="item"
            :heading-level="!isSmallMinus ? 4 : 3"
          />
        </div>
        <div
          v-else
          class="m-related-search-results__cta-button g-align-image-base__list-item--ignore"
        >
          <GuiLink
            class="m-related-search-results__cta-button-trigger"
            :to="{
              name: 'search',
              query: searchParamsForQuery
            }"
          >
            {{ browseAllCTALabel }}
          </GuiLink>
        </div>
      </template>
    </ListingCarousel>
  </div>
</template>

<script>
import {
  GuiLink,
  ImageAndCaption,
  ListingCarousel,
  localizationMixin,
  alignImageBaseMixin,
  breakpointsMixin
} from "@thegetty-private/getty-ui";

export default {
  name: "RelatedSearchResults",
  components: {
    GuiLink,
    ImageAndCaption,
    ListingCarousel
  },
  mixins: [localizationMixin, alignImageBaseMixin, breakpointsMixin],
  props: {
    baseLocaleKey: {
      type: String,
      default: "m-related-search-results"
    },
    /**
     * The suffix for the section that gets appended to `related-search-results-grid-` to allow for easier testing of this component
     */
    cypressSuffix: {
      type: String,
      default: () => {
        return undefined;
      }
    },
    /**
     * The heading for the section
     */
    heading: {
      type: String,
      required: true
    },
    /**
     * The path to send the search query
     */
    queryPath: {
      type: String,
      default: undefined,
      required: false
    },
    /**
     * additional search params
     */
    searchParams: {
      type: Object,
      default: () => {},
      required: false
    },
    /**
     * additional search params
     */
    searchResults: {
      type: Array,
      default: () => [],
      required: true
    },
    /**
     * Whether or not to show the total count of search results
     */
    showCount: {
      type: Boolean,
      default: false,
      required: false
    },
    /**
     * The subheading for the section
     */
    subheading: {
      type: [String, undefined],
      required: false,
      default: undefined
    },
    /**
     * the total # of items
     */
    total: {
      type: [Number, undefined],
      default: undefined,
      required: false
    },
    /**
     * Whether to display the browse all link in header
     */
    showBrowseAll: {
      type: Boolean,
      default: true
    }
  },
  computed: {
    /**
     * The label for searching all the nearby items
     *
     * @returns {string} - browse all nearby items label
     */
    browseAllCTALabel() {
      const displayTotal = this.total ? this.localizeNumber(this.total) : "";
      const localeKey = this.$te(
        `${this.baseLocaleKey}.${this.resultsTypeKey}_browse_all_cta`
      )
        ? `${this.resultsTypeKey}_browse_all_cta`
        : "default_browse_all_cta";
      return this.translatePart(localeKey, {
        length: displayTotal
      });
    },
    /**
     * The label for searching for all related items
     *
     * @returns {string} - browse all label
     */
    browseAllLabel() {
      return this.translatePart("browse_all");
    },
    /**
     * The locale subkey for the search results type
     *
     * @returns {string}
     */
    resultsTypeKey() {
      const resultsType = this.searchResults?.[0]?.type;
      return this.translatePart(resultsType.toLowerCase());
    },
    /**
     * The cypress tag for e2e testing
     *
     * @returns {string} - tag for e2e testing
     */
    cypressTag() {
      return "related-search-results-" + this.cypressSuffix;
    },
    /**
     * The related items to display
     *
     * @returns {array} - an array of items from the searchResults
     */
    items() {
      if (this.searchResults) {
        let items = [...this.searchResults];
        if (this.total > 6) {
          let self = this;
          items.push({
            total: self.total,
            params: self.searchParamsForQuery,
            cta: true
          });
        }

        return items;
      }

      return [];
    },
    /**
     * The params for the related items search query
     *
     * @returns {object} - search params object
     */
    searchParamsForQuery() {
      let queryParams = { ...this.searchParams };
      if (queryParams.filters) {
        Object.assign(queryParams, queryParams.filters);
        delete queryParams.filters;
      }

      if (queryParams.query) {
        queryParams.q = [...queryParams.query];
        delete queryParams.query;
      }

      if (
        ["api/relatedActors", "api/relatedCollections"].includes(
          queryParams?.path
        )
      ) {
        if (this.searchResults) {
          queryParams.id = this.searchResults.map((e) => e.id);
        }
      }

      delete queryParams.size;
      return queryParams;
    },
    /**
     * Specifies which icon/resource details to use if image is unavailable
     *
     * @returns {string} - an icon name
     */
    imageUnavailableVariant() {
      return this.heading == "people_heading" ? "actor" : "image";
    },
    /**
     * The full formatted text for the total count label
     *
     * @returns {string|undefined}
     */
    resultsCountLabel() {
      const localeKey = this.$te(
        `${this.baseLocaleKey}.${this.resultsTypeKey}_count`
      )
        ? `${this.resultsTypeKey}_count`
        : "default_count";
      return this.total == undefined
        ? undefined
        : this.translateCount(localeKey, this.total, {
            count: this.localizeNumber(this.total)
          });
    }
  }
};
</script>
<style lang="scss">
@import "./index";
</style>
